import APILibrary, { ClassExtender, GConstructor, GenerateResource } from './APILibrary';
import { GeographyResource } from './Geography';
import { ConnectorResource } from 'util/resources/Connector';
import { Base, Collection } from 'active-resource';
import { EmployerProfileResource } from 'util/resources/EmployerProfile';
import { LocationResource } from 'util/resources/Location';
import { SubscriptionResource } from 'util/resources/Subscription';
import { SubscriptionChangeResource } from 'util/resources/SubscriptionChange';
import { HiringHeroMetricResource } from './HiringHeroMetric';
import { PlanResource } from 'util/resources/Plan';

/**
 * Relationship classes
 */
export class EmployerAttributes {
  abilities: string[] = null;
  about: string = null;
  address: string = null;
  centerType: string = null;
  createdAt: string = null;
  educationPhilosophy: string = null;
  eligibleForFreeTrial: boolean = null;
  email: string = null;
  geographyId: string = null;
  hasPaymentMethod: boolean = null;
  id: string = null;
  languages: string = null;
  name: string = null;
  phoneNumber: string = null;
  profileImage: {
    downloadUrl: string;
  } = null;
  referrer: string = null;
  slug: string = null;
  website: string = null;
  zip: string = null;
  hiringHero: string = null;
  hiringHeroLastRanAt: string = null;
  responsive = false;
}

export class EmployerBelongsTo {
  geography: () => GeographyResource = () => new GeographyResource();
}

export class EmployerHasOne {}

export class EmployerHasMany {
  connectors: () => Collection<ConnectorResource> = () => new Collection<ConnectorResource>();
  employerProfiles: () => Collection<EmployerProfileResource> = () =>
    new Collection<EmployerProfileResource>();
  locations: () => Collection<LocationResource> = () => new Collection<LocationResource>();
  subscriptionChanges: () => Collection<SubscriptionChangeResource> = () =>
    new Collection<SubscriptionChangeResource>();
  subscriptions: () => Collection<SubscriptionResource> = () =>
    new Collection<SubscriptionResource>();
  hiringHeroMetrics: () => Collection<HiringHeroMetricResource> = () =>
    new Collection<HiringHeroMetricResource>();
}

/**
 * Utility interfaces
 */
export interface EmployerMethods {
  isEmployer: () => boolean;
  canLinkExternalJobPosts: () => boolean;
  hasActivePlan: () => boolean;
  hasValidLocations: () => boolean;
  findActiveSubscription: () => SubscriptionResource;
  findActivePlan: () => PlanResource;
  isFreePlan: () => boolean;
  linkToProfile: () => string;
  getSubscriptionChanges: () => SubscriptionChangeResource[];
}

export interface EmployerInterface
  extends EmployerAttributes,
    EmployerBelongsTo,
    EmployerHasMany,
    EmployerType {}

type EmployerType = GConstructor<EmployerInterface>;

export interface EmployerResource
  extends Base<EmployerAttributes>,
    EmployerInterface,
    EmployerMethods {}

/**
 * Resource methods
 */
const BaseClass = () =>
  GenerateResource<EmployerAttributes>({
    queryName: 'employers',
    className: 'Employer',
    AttributesClass: EmployerAttributes,
    BelongsToClasses: [
      {
        Klass: EmployerBelongsTo,
      },
    ],
    HasManyClasses: [
      {
        Klass: EmployerHasMany,
      },
    ],
  });

export const EmployerResourceClass = <TBase extends EmployerType>(BaseConstructor: TBase): TBase =>
  class extends BaseConstructor implements EmployerMethods {
    isEmployer() {
      return true;
    }

    canLinkExternalJobPosts() {
      return this.abilities?.includes('link_external_urls');
    }

    hasActivePlan() {
      return this.subscriptions()
        .toArray()
        .some((subscription) => ['active', 'trialing'].includes(subscription.status));
    }

    hasValidLocations() {
      return (
        this.locations()
          .toArray()
          .filter((loc) => loc.id)?.length > 0
      );
    }

    findActiveSubscription() {
      return this.subscriptions()
        .toArray()
        .find((subscription) => ['active', 'trialing'].includes(subscription.status));
    }

    findActivePlan() {
      return this.findActiveSubscription()?.plan();
    }

    isFreePlan() {
      const activeSub = this.findActiveSubscription();
      return activeSub?.freePlan;
    }

    linkToProfile() {
      return '/employer/' + this.slug;
    }

    getSubscriptionChanges() {
      return this.subscriptionChanges().toArray();
    }
  };

export class EmployerResource extends ClassExtender<EmployerInterface>(
  BaseClass,
  EmployerResourceClass,
) {}

/**
 * Resource instance
 */
export const Employer = APILibrary.createResource<EmployerResource>(EmployerResource);
